/**
 * Persistance locale sur la tablette.
 *
 * C'est ce qui rend le bouton « Reprendre » utile avant l'arrivée de l'API
 * (phase 5), et ce qui permettra au jeu de rester jouable quand le réseau du
 * collège tombe. Rien d'identifiant n'y est écrit : seulement le code d'îlot et
 * l'état de la partie.
 *
 * `localStorage` peut lever — navigation privée, quota, stockage bloqué. Chaque
 * accès est donc protégé : perdre la sauvegarde est ennuyeux, planter en pleine
 * séance l'est beaucoup plus.
 */
import { deserialize, serialize } from '@neopolis/engine';
import type { GameState } from '@neopolis/engine';

import type { SaveAdapter } from './save-adapter.js';

const PREFIXE = 'neopolis:partie:';
const CLE_DERNIER = 'neopolis:dernier-ilot';

function lire(cle: string): string | null {
  try {
    return globalThis.localStorage?.getItem(cle) ?? null;
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string): void {
  try {
    globalThis.localStorage?.setItem(cle, valeur);
  } catch {
    // Stockage indisponible : la partie continue, seule la reprise sera perdue.
  }
}

function supprimer(cle: string): void {
  try {
    globalThis.localStorage?.removeItem(cle);
  } catch {
    // Idem.
  }
}

export function createLocalStorageAdapter(): SaveAdapter {
  return {
    charger: (islandCode) => {
      const brut = lire(PREFIXE + islandCode);
      if (brut === null) {
        return Promise.resolve(null);
      }
      const relu = deserialize(brut);
      if (!relu.ok) {
        // Sauvegarde abîmée : on l'oublie plutôt que de bloquer l'îlot dessus.
        supprimer(PREFIXE + islandCode);
        return Promise.resolve(null);
      }
      return Promise.resolve(relu.state);
    },

    enregistrer: (state: GameState) => {
      ecrire(PREFIXE + state.islandCode, serialize(state));
      ecrire(CLE_DERNIER, state.islandCode);
      return Promise.resolve();
    },

    effacer: (islandCode) => {
      supprimer(PREFIXE + islandCode);
      if (lire(CLE_DERNIER) === islandCode) {
        supprimer(CLE_DERNIER);
      }
      return Promise.resolve();
    },

    dernierIlot: () => Promise.resolve(lire(CLE_DERNIER)),
  };
}
